import { useState, useEffect } from "react";
import { sendNotificationCampaign } from "../../../../Actions/AdminActions";
import { formatApiError } from "../../../../Actions/Utils";

const TARGET_STATES = ["Maharashtra", "Odisha"];

export default function SendCampaignForm({
  templates,
  templatesLoading,
  templatesError,
}) {
  const [templateName, setTemplateName] = useState("");
  const [targetStates, setTargetStates] = useState([]);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  function toggleState(state) {
    setTargetStates((prev) =>
      prev.includes(state) ? prev.filter((s) => s !== state) : [...prev, state]
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!templateName.trim() || sending) return;

    setSending(true);
    setError("");
    setSuccess(null);

    try {
      const res = await sendNotificationCampaign({
        template_name: templateName,
        target_states: targetStates,
      });

      if (res?.error) {
        setError(
          "Failed to send campaign. Please check your connection and try again."
        );
        return;
      }

      if (res?.status === 202) {
        setSuccess(res.data?.campaign_id || "unavailable");
        setTemplateName("");
        setTargetStates([]);
      } else {
        setError(
          formatApiError(res?.data?.detail) ||
            res?.data?.message ||
            "Failed to send campaign. Please try again."
        );
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900">Send Campaign</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Push a notification template to users. Delivery is queued and processed
          in the background.
        </p>
      </div>

      {error && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}
      {success && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Campaign sent successfully! Campaign ID: {success}
        </div>
      )}
      {templatesError && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {templatesError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="mb-5">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Template Name *
            </label>
            <select
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              disabled={templatesLoading || templates.length === 0}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="" disabled>
                {templatesLoading
                  ? "Loading templates…"
                  : templates.length === 0
                  ? "No templates available"
                  : "Select a template"}
              </option>
              {templates.map((template) => (
                <option key={template.id || template.name} value={template.name}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <span className="block text-xs font-medium text-gray-600 mb-2">
              Target States
            </span>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {TARGET_STATES.map((state) => (
                <label
                  key={state}
                  className="flex items-center gap-2 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={targetStates.includes(state)}
                    onChange={() => toggleState(state)}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">{state}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Leave both unchecked to send to users in all states.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!templateName.trim() || sending}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending…
                </>
              ) : (
                "Send Campaign"
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
